import { useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  User, 
  Phone, 
  Mail, 
  FileText, 
  Euro,
  Calendar,
  TrendingUp,
  Edit,
  Trash2,
  PlusCircle,
  Download,
  Share2
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { clientsApi, dashboardApi } from '@/lib/api';
import { formatCurrency, formatDate, generateAvatarInitials, getRandomColor } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { SalesChart } from '@/components/charts/sales-chart';

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'sales' | 'notes'>('overview');
  const tabs: Array<{ id: 'overview' | 'sales' | 'notes'; label: string; icon: LucideIcon }> = [
    { id: 'overview', label: 'Resumen', icon: TrendingUp },
    { id: 'sales', label: 'Ventas', icon: Euro },
    { id: 'notes', label: 'Notas', icon: FileText },
  ];

  const { data: clientData, isLoading: clientLoading } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clientsApi.getById(id!),
    enabled: !!id,
  });

  const { data: performanceData, isLoading: performanceLoading } = useQuery({
    queryKey: ['client-performance', id],
    queryFn: () => dashboardApi.getClientPerformance(id!),
    enabled: !!id,
  });

  const client = clientData?.data;
  const performance = performanceData?.data;

  if (clientLoading || performanceLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 dark:bg-gray-800 rounded-lg animate-pulse"></div>
        <div className="h-64 bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
          <User className="w-8 h-8 text-red-500" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Cliente no encontrado
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          El cliente que buscas no existe o ha sido eliminado.
        </p>
        <Button onClick={() => navigate('/clients')}>
          <ArrowLeft className="w-5 h-5 mr-2" />
          Volver a Clientes
        </Button>
      </div>
    );
  }

  const stats = [
    {
      title: 'Ventas Totales',
      value: performance?.stats.totalSales || 0,
      icon: TrendingUp,
      color: 'bg-blue-500',
    },
    {
      title: 'Ingresos Totales',
      value: performance?.stats.totalAmount || 0,
      icon: Euro,
      color: 'bg-emerald-500',
      formatted: formatCurrency(performance?.stats.totalAmount || 0),
    },
    {
      title: 'Promedio por Venta',
      value: performance?.stats.averageSale || 0,
      icon: Euro,
      color: 'bg-purple-500',
      formatted: formatCurrency(performance?.stats.averageSale || 0),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/clients')}
            className="p-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              {client.name}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Detalles del cliente y estadísticas
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline">
            <Share2 className="w-5 h-5 mr-2" />
            Compartir
          </Button>
          <Button variant="outline">
            <Download className="w-5 h-5 mr-2" />
            Exportar
          </Button>
          <Button className="bg-gradient-to-r from-emerald-500 to-green-600">
            <PlusCircle className="w-5 h-5 mr-2" />
            Nueva Venta
          </Button>
        </div>
      </div>

      {/* Client card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-8 text-white"
      >
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-center space-x-6">
            <div className={`w-24 h-24 rounded-2xl ${getRandomColor()} flex items-center justify-center text-white text-3xl font-bold`}>
              {generateAvatarInitials(client.name)}
            </div>
            <div>
              <h2 className="text-2xl font-bold">{client.name}</h2>
              <div className="flex flex-wrap gap-4 mt-3">
                <div className="flex items-center">
                  <FileText className="w-5 h-5 mr-2 opacity-80" />
                  <span>CIF: {client.cif}</span>
                </div>
                {client.phone && (
                  <div className="flex items-center">
                    <Phone className="w-5 h-5 mr-2 opacity-80" />
                    <span>{client.phone}</span>
                  </div>
                )}
                {client.email && (
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 mr-2 opacity-80" />
                    <span>{client.email}</span>
                  </div>
                )}
              </div>
              {client.notes && (
                <p className="mt-4 opacity-90 max-w-2xl">{client.notes}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" className="bg-white/20 hover:bg-white/30 text-white border-white/30">
              <Edit className="w-5 h-5 mr-2" />
              Editar
            </Button>
            <Button variant="outline" className="bg-white/20 hover:bg-white/30 text-white border-white/30 hover:text-red-100">
              <Trash2 className="w-5 h-5 mr-2" />
              Eliminar
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="w-5 h-5 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {stat.title}
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                  {stat.formatted || stat.value}
                </p>
              </div>
              <div className={`p-3 rounded-xl ${stat.color} bg-opacity-10`}>
                <stat.icon className={`w-6 h-6 ${stat.color.replace('bg-', 'text-')}`} />
              </div>
            </div>
            {stat.title === 'Promedio por Venta' && performance?.stats.lastSaleDate && (
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>Última venta: {formatDate(performance.stats.lastSaleDate)}</span>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Content based on active tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sales chart */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Evolución de Ventas
            </h3>
            <div className="h-64">
              <SalesChart
                data={performance?.monthlyTrend?.map(item => ({
                  date: item.month,
                  amount: item.amount,
                })) || []}
              />
            </div>
          </motion.div>

          {/* Recent sales */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Ventas Recientes
              </h3>
              <Button variant="ghost" size="sm">
                Ver todas
              </Button>
            </div>
            <div className="space-y-4">
              {performance?.recentSales?.slice(0, 5).map((sale) => (
                <div
                  key={sale.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {sale.formattedAmount}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {sale.formattedDate}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {sale.notes ? sale.notes.substring(0, 30) + '...' : 'Sin notas'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      )}

      {activeTab === 'sales' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
            Historial de Ventas
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                    Fecha
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                    Monto
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                    Notas
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {performance?.recentSales?.map((sale) => (
                  <tr
                    key={sale.id}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="py-4 px-4">
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <Calendar className="w-4 h-4 mr-2" />
                        <span>{sale.formattedDate}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center font-bold text-emerald-600 dark:text-emerald-400">
                        <Euro className="w-4 h-4 mr-2" />
                        {sale.formattedAmount}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-gray-600 dark:text-gray-400 max-w-xs truncate">
                        {sale.notes || 'Sin notas'}
                      </p>
                    </td>
                    <td className="py-4 px-4">
                      <Button variant="ghost" size="sm">
                        Ver detalles
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {activeTab === 'notes' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Notas del Cliente
            </h3>
            <Button>
              <PlusCircle className="w-5 h-5 mr-2" />
              Agregar Nota
            </Button>
          </div>
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-800 dark:text-blue-400">
                  Nota principal
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(client.createdAt)}
                </span>
              </div>
              <p className="text-gray-700 dark:text-gray-300">
                {client.notes || 'No hay notas registradas para este cliente.'}
              </p>
            </div>
            
            {/* Additional notes would go here */}
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Agrega notas adicionales sobre este cliente</p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
